// ============================================================================
// Button Component
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Base button component with variants
// ============================================================================

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * Button variants
 */
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';

/**
 * Button sizes
 */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Button props
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loading?: boolean; // Alias for isLoading
  isFullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children: ReactNode;
}

/**
 * Base styles for all buttons
 */
const baseStyles = `
  inline-flex items-center justify-center font-medium rounded-[var(--radius-md)]
  transition-all duration-[120ms]
  focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-accent)]
  disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
`;

/**
 * Variant styles
 */
// Spec 197 design blueprint: variants are token-driven so they track the
// single design-token source (Azure light + dark) with no per-variant dark:
// overrides. Accent = command-blue; status colors are mode-agnostic.
const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-[var(--color-accent)] text-white shadow-[var(--shadow-xs)]
    hover:bg-[var(--color-accent-hover)] hover:shadow-[var(--shadow-md)] hover:-translate-y-px
    active:translate-y-0 active:shadow-[var(--shadow-xs)]
  `,
  secondary: `
    bg-[var(--color-bg-card)] text-[var(--color-text-primary)]
    border border-[var(--color-border)] shadow-[var(--shadow-xs)]
    hover:bg-[var(--color-bg-subtle)] hover:border-[var(--color-text-secondary)]
  `,
  outline: `
    border border-[var(--color-accent)] text-[var(--color-accent)] bg-transparent shadow-[var(--shadow-xs)]
    hover:bg-[var(--color-accent)] hover:text-white hover:shadow-[var(--shadow-md)] hover:-translate-y-px
    active:translate-y-0 active:shadow-[var(--shadow-xs)]
    focus:ring-[var(--color-accent)]
  `,
  ghost: `
    text-[var(--color-text-secondary)] bg-transparent
    hover:bg-[var(--color-bg-subtle)]
    focus:ring-[var(--color-accent)]
  `,
  danger: `
    bg-[var(--color-status-danger)] text-white shadow-[var(--shadow-xs)]
    hover:brightness-110 hover:shadow-[var(--shadow-md)] hover:-translate-y-px
    active:translate-y-0 active:shadow-[var(--shadow-xs)]
  `,
  success: `
    bg-[var(--color-status-success)] text-white shadow-[var(--shadow-xs)]
    hover:brightness-110 hover:shadow-[var(--shadow-md)] hover:-translate-y-px
    active:translate-y-0 active:shadow-[var(--shadow-xs)]
  `,
};

/**
 * Size styles
 */
const sizeStyles: Record<ButtonSize, string> = {
  xs: 'text-xs px-2 py-1 gap-1',
  sm: 'text-sm px-3 py-1.5 gap-1.5',
  md: 'text-sm px-4 py-2 gap-2',
  lg: 'text-base px-5 py-2.5 gap-2',
  xl: 'text-lg px-6 py-3 gap-2.5',
};

/**
 * Loading spinner component
 */
const LoadingSpinner = ({ size }: { size: ButtonSize }) => {
  const spinnerSizes: Record<ButtonSize, string> = {
    xs: 'h-3 w-3',
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6',
  };

  return (
    <svg
      className={clsx('animate-spin', spinnerSizes[size])}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

/**
 * Button Component
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      loading,
      isFullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const showLoading = isLoading || loading;
    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          isFullWidth && 'w-full',
          className
        )}
        disabled={disabled || showLoading}
        {...props}
      >
        {showLoading && <LoadingSpinner size={size} />}
        {!showLoading && leftIcon}
        {children}
        {!showLoading && rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
